/**
 * [ECOMHARD] Brindes do Monte seu Kit — validação do carrinho Ajax.
 * Cada montagem com brindes recebe um identificador único e uma lista dos itens
 * obrigatórios, por variante. Nunca manipula os brindes VIP/Free Gift antigos.
 * Observação: proteção no tema; não é uma regra antifraude no checkout/API.
 */
(() => {
  if (window.EcomhardKitGifts?.initialized) return;

  const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const cartUrl = `${rootUrl}cart.js`;
  const changeUrl = `${rootUrl}cart/change.js`;
  let active = null;
  let pending = false;
  let timer = null;
  let lastHasGift = false;
  let checkoutResuming = false;
  let checkoutValidating = false;

  const properties = (item) => item?.properties || {};
  const isKitGift = (item) =>
    String(properties(item)._ecomhard_kit_gift || '') === 'true' &&
    Boolean(properties(item)._ecomhard_kit_group);

  function requiredVariants(raw) {
    if (typeof raw !== 'string' || !raw.length) return null;
    const requirements = new Map();
    for (const entry of raw.split('|')) {
      const match = /^(\d+):(\d+)$/.exec(entry);
      if (!match || !Number(match[2])) return null;
      requirements.set(match[1], (requirements.get(match[1]) || 0) + Number(match[2]));
    }
    return requirements.size ? requirements : null;
  }

  function diagnose(cart) {
    const lines = Array.isArray(cart?.items) ? cart.items : [];
    const gifts = lines.filter(isKitGift);
    const changes = [];
    for (const gift of gifts) {
      const group = String(properties(gift)._ecomhard_kit_group || '');
      const required = requiredVariants(String(properties(gift)._ecomhard_kit_requires || ''));
      const groupedPaid = new Map();
      for (const line of lines) {
        if (isKitGift(line) || String(properties(line)._ecomhard_kit_group || '') !== group) continue;
        const id = String(line.variant_id || line.id || '');
        groupedPaid.set(id, (groupedPaid.get(id) || 0) + Number(line.quantity || 0));
      }

      // Se a metadata ficou inválida, a opção mais segura é retirar o brinde.
      const eligible = required && [...required].every(([id, qty]) => (groupedPaid.get(id) || 0) >= qty);
      const expectedQuantity = Number(properties(gift)._ecomhard_kit_gift_qty);
      const quantity = eligible && Number.isSafeInteger(expectedQuantity) && expectedQuantity > 0
        ? expectedQuantity : 0;
      if (!eligible || Number(gift.quantity || 0) !== quantity) {
        changes.push({ key: gift.key, quantity, group, reason: eligible ? 'quantity' : 'missing-required-item' });
      }
    }
    return { hasGifts: gifts.length > 0, changes };
  }

  async function getCart() {
    const response = await fetch(cartUrl, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`Carrinho indisponível: ${response.status}`);
    return response.json();
  }

  async function changeLine(key, quantity) {
    const response = await fetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Erro ao corrigir brinde do kit');
  }

  function notifyCart(cart) {
    // O Minimog atualiza a página /cart via PubSub. O drawer precisa atualizar
    // suas seções mesmo se o usuário estiver com ele aberto.
    try {
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer?.onCartDrawerUpdate) drawer.onCartDrawerUpdate(true);
    } catch (e) { console.warn('[ECOMHARD] Falha ao atualizar drawer:', e); }
    try {
      const cartEvent = window.MinimogTheme?.pubSubEvents?.cartUpdate;
      if (cartEvent && window.MinimogEvents?.emit) {
        window.MinimogEvents.emit(cartEvent, { ...cart, source: 'ecomhard-kit-gifts' });
      } else if (document.querySelector('m-cart')?.onCartUpdate) {
        document.querySelector('m-cart').onCartUpdate(true);
      }
    } catch (e) { console.warn('[ECOMHARD] Falha ao atualizar carrinho:', e); }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'ecomhard-kit-gifts' } }));
  }

  async function sync() {
    if (active) {
      pending = true;
      return active;
    }
    active = (async () => {
      let cart = await getCart();
      let state = diagnose(cart);
      lastHasGift = state.hasGifts;
      if (!state.changes.length) return { cart, changed: false, valid: true };

      // Refaz a leitura antes de cada alteração: keys e quantidades podem mudar
      // após o Shopify reordenar linhas ou mesclar uma variante.
      let changed = false;
      for (let attempt = 0; attempt < 8; attempt++) {
        state = diagnose(cart);
        if (!state.changes.length) break;
        const next = state.changes[0];
        if (!next.key) throw new Error('Brinde sem identificador de linha');
        await changeLine(next.key, next.quantity);
        changed = true;
        cart = await getCart();
      }
      state = diagnose(cart);
      lastHasGift = state.hasGifts;
      if (changed) notifyCart(cart);
      return { cart, changed, valid: state.changes.length === 0 };
    })();

    try {
      return await active;
    } finally {
      active = null;
      if (pending) {
        pending = false;
        schedule(100);
      }
    }
  }

  function schedule(delay = 90) {
    clearTimeout(timer);
    timer = setTimeout(() => sync().catch((error) => {
      console.error('[ECOMHARD] Não foi possível sincronizar o brinde do kit:', error);
    }), delay);
  }

  function showCheckoutError() {
    const message = 'Não foi possível conferir os brindes do kit. Tente novamente.';
    try {
      if (window.MinimogTheme?.Notification?.show) {
        window.MinimogTheme.Notification.show({ type: 'warning', message });
      } else window.alert(message);
    } catch (_) { window.alert(message); }
  }

  // Check-out: verifica novamente antes de enviar, inclusive se o cliente
  // clicou logo após remover o produto obrigatório.
  document.addEventListener('submit', (event) => {
    if (checkoutResuming || checkoutValidating) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter || form.querySelector('[name="checkout"]');
    if (!submitter || submitter.name !== 'checkout') return;
    if (!lastHasGift && !document.querySelector('[data-ecomhard-kit-gift="true"]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutValidating = true;
    submitter.disabled = true;
    sync().then((state) => {
      if (!state.valid) throw new Error('O carrinho não está válido');
      checkoutResuming = true;
      submitter.disabled = false;
      try {
        // A correção pode redesenhar o carrinho: procurar o formulário atual.
        const refreshedForm = form.isConnected ? form : document.querySelector('form[action*="/cart"]');
        const refreshedSubmitter = refreshedForm && (
          refreshedForm.querySelector('[name="checkout"]') || null
        );
        if (refreshedForm && refreshedSubmitter) {
          refreshedForm.requestSubmit(refreshedSubmitter);
        } else {
          // Sem formulário atual, a validação já concluiu e o carrinho foi corrigido.
          window.location.assign(`${rootUrl}checkout`);
        }
      } finally {
        checkoutResuming = false;
      }
    }).catch((error) => {
      console.error('[ECOMHARD] Validação de kit antes do checkout:', error);
      submitter.disabled = false;
      showCheckoutError();
    }).finally(() => { checkoutValidating = false; });
  }, true);

  document.addEventListener('click', (event) => {
    if (checkoutValidating) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor || !/\/(?:checkouts?)(?:[/?#]|$)/.test(anchor.pathname)) return;
    if (!lastHasGift && !document.querySelector('[data-ecomhard-kit-gift="true"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutValidating = true;
    sync().then((state) => {
      if (!state.valid) throw new Error('O carrinho não está válido');
      window.location.assign(anchor.href);
    }).catch((error) => {
      console.error('[ECOMHARD] Validação antes do checkout:', error);
      showCheckoutError();
    }).finally(() => { checkoutValidating = false; });
  }, true);

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source === 'ecomhard-kit-gifts') return;
    schedule();
  });
  document.addEventListener('cart:update-finished', () => schedule());
  document.addEventListener('DOMContentLoaded', () => schedule(0));
  window.addEventListener('pageshow', () => schedule(0));
  window.addEventListener('focus', () => schedule(200));

  try {
    if (window.MinimogEvents?.subscribe && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, (event) => {
        if (event?.source !== 'ecomhard-kit-gifts') schedule();
      });
    }
  } catch (_) { /* O CustomEvent cobre atualizações do tema. */ }

  window.EcomhardKitGifts = {
    initialized: true,
    diagnose,
    sync,
    status: async () => diagnose(await getCart())
  };
})();
